/**
 * SWYM UTILITIES
 *
 * Comprehensive integration layer between SWYM's wishlist platform and Shopify's native APIs.
 * Provides seamless wishlist management, event handling, and component integration capabilities.
 *
 * Used by: swym-custom-add-to-wishlist.js, product components, collection pages
 *
 * Key Features:
 * - Centralized SWYM API management with proper error handling
 * - Promise-based API wrappers for modern async/await usage
 * - Event-driven architecture for component communication
 * - Default wishlist management with automatic creation/cleanup
 * - Component initialization and state management
 * - Success notification handling with customizable UI
 *
 * Client-Specific Customizations (Greyson):
 * - Single default wishlist strategy ("My Greyson Wishlist")
 * - Automatic cleanup of extra wishlists
 * - Custom success notifications with product imagery
 * - Integration with cart drawer for seamless UX
 *
 * @namespace SwymUtilities
 */

/**
 * Configuration constants
 * @type {Object}
 * @private
 */
const _config = {
  DEBUG: false,
  DEFAULT_WISHLIST_NAME: "My Greyson Wishlist",
  MAX_RETRIES: 50,
  RETRY_INTERVAL: 100,
  TIMEOUT: 10000,
  COMPONENT_SELECTOR: 'swym-custom-add-to-wishlist'
};

/**
 * Global state management
 * @type {Object}
 * @private
 */
let _state = {
  isInitialized: false,
  swat: null,
  wishlists: [],
  defaultWishlist: {},
  initPromise: null
};

/**
 * Wait for SWYM library to be available with retry mechanism
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {number} options.retryInterval - Interval between retries in ms
 * @param {number} options.timeout - Overall timeout in ms
 * @returns {Promise<boolean>} - Resolves when SWYM is available
 * @private
 */
const _waitForSwym = ({
  maxRetries = _config.MAX_RETRIES,
  retryInterval = _config.RETRY_INTERVAL,
  timeout = _config.TIMEOUT
} = {}) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("SWYM library not available after waiting"));
    }, timeout);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    const checkForSwym = (retries = 0) => {
      if (typeof window._swat !== "undefined" || typeof window.swat !== "undefined") {
        cleanup();
        resolve(true);
      } else if (retries < maxRetries) {
        setTimeout(() => {
          checkForSwym(retries + 1);
        }, retryInterval);
      } else {
        cleanup();
        reject(new Error("SWYM library not available after waiting"));
      }
    };

    checkForSwym();
  });
};

/**
 * Promise wrapper for SWYM API calls
 * @param {string} method - The SWYM method name
 * @param {...any} args - Arguments to pass to the method (excluding callbacks)
 * @returns {Promise<any>} - Promise that resolves with the API response
 * @private
 */
const _promisifySwymCall = (method, ...args) => {
  return new Promise((resolve, reject) => {
    if (!_state.swat || typeof _state.swat[method] !== 'function') {
      reject(new Error(`SWYM method ${method} not available`));
      return;
    }

    const successCallback = (response) => {
      if (_config.DEBUG) {
        console.log(`SWYM ${method} success:`, response);
      }
      resolve(response);
    };

    const errorCallback = (error) => {
      console.error(`SWYM ${method} error:`, error);
      reject(new Error(`SWYM ${method} failed: ${error.message || error}`));
    };

    try {
      _state.swat[method](...args, successCallback, errorCallback);
    } catch (error) {
      reject(new Error(`SWYM ${method} call failed: ${error.message}`));
    }
  });
};

/**
 * Create a new wishlist
 * @param {Object} listConfig - Configuration for the new list
 * @param {string} listConfig.lname - List name
 * @returns {Promise<Object>} - Promise that resolves with the created list
 */
export const swymCreateList = (listConfig) => {
  return _promisifySwymCall('createList', listConfig);
};

/**
 * Fetch all wishlist shells for the current user
 * @returns {Promise<Array>} - Promise that resolves with array of list objects
 */
export const swymFetchLists = () => {
  return new Promise((resolve, reject) => {
    if (!_state.swat) {
      reject(new Error('SWYM not initialized'));
      return;
    }

    _state.swat.fetchLists({
      callbackFn: resolve,
      errorFn: reject
    });
  });
};

/**
 * Fetch detailed data for specific lists
 * @param {Array} lists - Array of list objects to fetch details for
 * @returns {Promise<Array>} - Promise that resolves with detailed list data
 */
export const swymFetchListDetails = (lists) => {
  const detailsPromises = lists.map((list) => {
    return _promisifySwymCall('fetchListDetails', list?.lid);
  });
  return Promise.all(detailsPromises);
};

/**
 * Add products to a wishlist
 * @param {string} listId - The list ID
 * @param {Array} products - Array of product objects to add
 * @returns {Promise<Object>} - Promise that resolves with the API response
 */
export const swymAddProductsToList = (listId, products) => {
  return _promisifySwymCall('addProductsToList', listId, products);
};

/**
 * Remove products from a wishlist
 * @param {string} listId - The list ID
 * @param {Array} products - Array of product objects to remove
 * @returns {Promise<Object>} - Promise that resolves with the API response
 */
export const swymRemoveProductsFromList = (listId, products) => {
  return _promisifySwymCall('removeProductsFromList', listId, products);
};

/**
 * Delete a wishlist
 * @param {string} listId - The list ID to delete
 * @returns {Promise<Object>} - Promise that resolves with the API response
 */
export const swymDeleteList = (listId) => {
  return _promisifySwymCall('deleteList', listId);
};

/**
 * Search for a wishlist by name
 * @param {string} desiredListName - The name to search for
 * @returns {Object} - The found list or empty object
 */
export const swymFindWishlistByName = (desiredListName) => {
  return _state.wishlists.find((list) => list.list?.lname === desiredListName) || {};
};

/**
 * Get the current default wishlist
 * @returns {Object} - The default wishlist object
 */
export const swymGetDefaultWishlist = () => {
  return _state.defaultWishlist;
};

/**
 * Get all current wishlists
 * @returns {Array} - Array of all wishlist objects
 */
export const swymGetAllWishlists = () => {
  return _state.wishlists;
};

/**
 * Check if products are in a specific wishlist
 * @param {Array} products - Array of product objects to check
 * @param {Object} wishlist - The wishlist to check against
 * @returns {boolean} - True if all products are in the wishlist
 */
export const swymProductsAreInList = (products, wishlist = _state.defaultWishlist) => {
  if (!wishlist?.items || !Array.isArray(products) || products.length === 0) {
    return false;
  }

  let matchingProductsCount = 0;
  const productsToMatchCount = products.length;

  for (const product of products) {
    if (wishlist.items.some(item => item.epi === product.epi)) {
      matchingProductsCount++;
    }
    if (matchingProductsCount === productsToMatchCount) {
      return true;
    }
  }

  return false;
};

/**
 * Create and set the default wishlist
 * @param {string} listName - Name for the new default list
 * @returns {Promise<Object>} - Promise that resolves with the created list
 * @private
 */
const _createAndSetDefaultWishlist = async (listName) => {
  const newListConfig = { lname: listName };

  try {
    const newList = await swymCreateList(newListConfig);
    if (_config.DEBUG) {
      console.log("SWYM utilities: created default wishlist", newList);
    }

    const newListDetails = await swymFetchListDetails([newList]);
    _state.wishlists.push(newListDetails[0]);
    _state.defaultWishlist = newListDetails[0];

    return newListDetails[0];
  } catch (error) {
    console.error("Error creating default wishlist:", error);
    throw error;
  }
};

/**
 * Delete all wishlists except the one with the specified name
 * @param {string} lname - Name of the list to keep
 * @returns {Promise<Array>} - Promise that resolves when deletion is complete
 */
export const swymDeleteAllListsExcept = async (lname) => {
  if (_config.DEBUG) {
    console.log(`SWYM utilities: Starting deletion of all wishlists except ${lname}:`, _state.wishlists);
  }

  const deletionPromises = _state.wishlists
    .filter(wishlist => wishlist.list?.lname !== lname)
    .map(wishlist => {
      return swymDeleteList(wishlist.list?.lid).catch(error => {
        console.error("Failed to delete list:", wishlist.list?.lid, error);
        return { error, listId: wishlist.list?.lid };
      });
    });

  return Promise.all(deletionPromises);
};

/**
 * Delete all wishlists for the current user
 * @returns {Promise<Array>} - Promise that resolves when deletion is complete
 */
export const swymDeleteAllLists = async () => {
  if (_config.DEBUG) {
    console.log("SWYM utilities: Starting deletion of all wishlists:", _state.wishlists);
  }

  const deletionPromises = _state.wishlists.map(wishlist => {
    return swymDeleteList(wishlist.list?.lid).catch(error => {
      console.error("Failed to delete list:", wishlist.list?.lid, error);
      return { error, listId: wishlist.list?.lid };
    });
  });

  return Promise.all(deletionPromises);
};

/**
 * Initialize wishlists - fetch existing lists and set up default
 * @returns {Promise<void>} - Promise that resolves when initialization is complete
 * @private
 */
const _initializeWishlists = async () => {
  try {
    // Fetch all lists from SWYM
    let lists = await swymFetchLists();

    // Get detailed data for all lists
    _state.wishlists = lists.length > 0 ? await swymFetchListDetails(lists) : [];

    // Clean up - delete any list that is not the default wishlist name
    await swymDeleteAllListsExcept(_config.DEFAULT_WISHLIST_NAME);

    // Refresh the list after cleanup
    lists = await swymFetchLists();
    _state.wishlists = lists.length > 0 ? await swymFetchListDetails(lists) : [];

    // Look for the default wishlist name and set it as default
    _state.defaultWishlist = swymFindWishlistByName(_config.DEFAULT_WISHLIST_NAME);

    // Check if the default list exists, create if not found
    if (Object.keys(_state.defaultWishlist).length === 0) {
      if (_config.DEBUG) {
        console.log(`SWYM utilities: ${_config.DEFAULT_WISHLIST_NAME} not found, creating new...`);
      }
      await _createAndSetDefaultWishlist(_config.DEFAULT_WISHLIST_NAME);
    }

    if (_config.DEBUG) {
      console.log("SWYM utilities: all wishlists:", _state.wishlists);
      console.log("SWYM utilities: default wishlist:", _state.defaultWishlist);
    }
  } catch (error) {
    console.error("Failed to initialize wishlists:", error);
    throw error;
  }
};

/**
 * Initialize all custom wishlist components on the page
 * @private
 */
const _initializeCustomButtons = () => {
  const wishlistButtons = document.querySelectorAll(_config.COMPONENT_SELECTOR);

  if (wishlistButtons.length > 0) {
    if (_config.DEBUG) {
      console.log(`SWYM utilities: initializing ${wishlistButtons.length} wishlist components`);
    }

    wishlistButtons.forEach((button) => {
      if (typeof button.init === "function") {
        button.init();
      }
    });
  }
};

/**
 * Refresh all custom wishlist components on the page
 */
export const swymRefreshComponents = () => {
  const wishlistButtons = document.querySelectorAll(_config.COMPONENT_SELECTOR);

  wishlistButtons.forEach((button) => {
    if (typeof button.refreshComponentState === "function") {
      button.refreshComponentState();
    }
  });
};

/**
 * Handle items added to cart from wishlist
 * @param {CustomEvent} event - The SWYM added to cart event
 * @private
 */
const _handleAddedToCart = (event) => {
  if (_config.DEBUG) {
    console.log("SWYM utilities: Added item(s) to cart from wishlist", event);
  }

  // Open cart drawer if available
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer && typeof cartDrawer.refresh === "function") {
    cartDrawer.refresh();
    if (typeof cartDrawer.open === "function") {
      cartDrawer.open();
    }
  }

};

/**
 * Handle items removed from wishlist
 * @param {CustomEvent} event - The SWYM removed from wishlist event
 * @private
 */
const _handleRemovedFromWishlist = async (event) => {
  if (_config.DEBUG) {
    console.log("SWYM utilities: Item removed from wishlist", event);
  }

  // Refresh wishlist data
  await _initializeWishlists();

  // Refresh all components
  swymRefreshComponents();
};

/**
 * Handle items added to wishlist
 * @param {CustomEvent} event - The SWYM added to wishlist event
 * @private
 */
const _handleAddedToWishlist = async (event) => {
  if (_config.DEBUG) {
    console.log("SWYM utilities: Item added to wishlist", event);
  }

  // Refresh wishlist data
  await _initializeWishlists();

  // Refresh all components
  swymRefreshComponents();
};

/**
 * Set up event listeners for SWYM events
 * @private
 */
const _setEventListeners = () => {
  if (!_state.swat?.evtLayer) {
    console.warn("SWYM event layer not available");
    return;
  }

  _state.swat.evtLayer.addEventListener("sw:addedtocart", _handleAddedToCart);
  _state.swat.evtLayer.addEventListener("sw:removedfromwishlist", _handleRemovedFromWishlist);
  _state.swat.evtLayer.addEventListener("sw:addedtowishlist", _handleAddedToWishlist);

  if (_config.DEBUG) {
    console.log("SWYM utilities: Event listeners set up");
  }
};

/**
 * Build success notification HTML for added items
 * @param {Array} products - Array of product objects that were added
 * @param {Object} context - Context information (productOverview, bundleItem, etc.)
 * @returns {string} - HTML string for the notification
 */
export const swymBuildSuccessNotification = (products, context = {}) => {
  let itemsHTML = '';

  products.forEach((product) => {
    if (product && product.id) {
      const imageUrl = product.featured_media || product.featured_media_url || '';
      const productTitle = product.product_title || product.title || 'Product';
      const variantTitle = product.title || product.variant_title || '';

      itemsHTML += `
        <div id="swym-custom-notification-content-${product.id}" class="swym-custom-notification-content">
          <div class="swym-custom-image-container">
            ${imageUrl ? `<img class="swym-custom-image" src="${imageUrl}" alt="wishlisted product image"/>` : ''}
          </div>
          <div class="swym-custom-title-container">
            <strong>${productTitle}${variantTitle ? ' - ' + variantTitle : ''}</strong> has been added to <strong>1 list</strong> successfully
          </div>
        </div>
      `;
    }
  });

  const notificationHTML = `
    <style>
      .swym-notification-success-inner .swym-image { display: none !important }
      .swym-custom-notification-content { display: flex; gap: 10px }
      .swym-notification-success-inner .swym-details-container .swym-details { display: flex; flex-flow: row wrap; gap: 10px }
      .swym-custom-notification-content .swym-custom-image-container { min-height: 50px; min-width: 50px; max-height: 50px; max-width: 50px }
      .swym-custom-notification-content .swym-custom-image-container .swym-custom-image { width: 100%; height: 100%; object-fit: cover }
      .swym-custom-notification-content .swym-custom-title-container { font-weight: 500; font-size: 14px }
    </style>
    ${itemsHTML}
  `;

  return notificationHTML;
};

/**
 * Show success notification using SWYM's UI
 * @param {string} message - HTML message to display
 */
export const swymShowSuccessNotification = (message) => {
  if (_state.swat?.ui?.uiRef?.showSuccessNotification) {
    _state.swat.ui.uiRef.showSuccessNotification({ message });
  } else {
    console.warn("SWYM success notification UI not available");
  }
};

/**
 * Main initialization function called by SWYM when ready
 * @param {Object} swat - The SWYM API object
 * @returns {Promise<void>} - Promise that resolves when initialization is complete
 */
const swymInitialize = async (swat) => {
  if (_state.isInitialized) {
    if (_config.DEBUG) {
      console.log("SWYM utilities: Already initialized, skipping");
    }
    return;
  }

  if (!swat) {
    console.error("SWYM utilities: No swat object provided");
    return;
  }

  try {
    if (_config.DEBUG) {
      console.log("SWYM utilities: Starting initialization");
    }

    // Store SWYM API reference
    _state.swat = swat;

    // Make SWYM available globally for backward compatibility
    window.swat = swat;

    // Initialize wishlists
    await _initializeWishlists();

    // Set up event listeners
    _setEventListeners();

    // Initialize custom components
    _initializeCustomButtons();

    // Mark as initialized
    _state.isInitialized = true;

    if (_config.DEBUG) {
      console.log("SWYM utilities: Initialization complete");
    }
  } catch (error) {
    console.error("SWYM utilities: Initialization failed", error);
    throw error;
  }
};

/**
 * Get initialization status
 * @returns {boolean} - True if SWYM is initialized
 */
export const swymIsInitialized = () => {
  return _state.isInitialized;
};

/**
 * Wait for SWYM to be initialized
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<boolean>} - Promise that resolves when initialized
 */
export const swymWaitForInitialization = (timeout = _config.TIMEOUT) => {
  if (_state.isInitialized) {
    return Promise.resolve(true);
  }

  if (_state.initPromise) {
    return _state.initPromise;
  }

  _state.initPromise = new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("SWYM initialization timeout"));
    }, timeout);

    const checkInitialized = () => {
      if (_state.isInitialized) {
        clearTimeout(timeoutId);
        resolve(true);
      } else {
        setTimeout(checkInitialized, 100);
      }
    };

    checkInitialized();
  });

  return _state.initPromise;
};

/**
 * Export configuration for external access
 */
export const swymConfig = {
  DEBUG: _config.DEBUG,
  DEFAULT_WISHLIST_NAME: _config.DEFAULT_WISHLIST_NAME
};

// Register the initialization callback with SWYM
if (typeof window !== 'undefined') {
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(swymInitialize);

  // Export utilities to global scope for backward compatibility
  window.swymUtilities = {
    initialize: swymInitialize,
    isInitialized: swymIsInitialized,
    waitForInitialization: swymWaitForInitialization,
    getDefaultWishlist: swymGetDefaultWishlist,
    getAllWishlists: swymGetAllWishlists,
    findWishlistByName: swymFindWishlistByName,
    productsAreInList: swymProductsAreInList,
    addProductsToList: swymAddProductsToList,
    removeProductsFromList: swymRemoveProductsFromList,
    refreshComponents: swymRefreshComponents,
    buildSuccessNotification: swymBuildSuccessNotification,
    showSuccessNotification: swymShowSuccessNotification,
    deleteAllLists: swymDeleteAllLists,
    deleteAllListsExcept: swymDeleteAllListsExcept,
    config: swymConfig
  };

  if (_config.DEBUG) {
    console.log("SWYM utilities: Registered with SwymCallbacks and global scope");
  }
}
